import * as React from "react";
import {
  Box,
  Breadcrumbs,
  Container,
  CssBaseline,
  Grid,
  Toolbar,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import GroupsIcon from "@mui/icons-material/Groups";
import EventIcon from "@mui/icons-material/Event";
import PeopleIcon from "@mui/icons-material/People";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import AdminPageHeader from "../components/AdminPageHeader";
import { useNavigate } from "react-router-dom";

const circleFeatures = [
  {
    id: "events",
    title: "Events",
    description: "View and manage Circle community events",
    icon: <EventIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/circle/events",
    status: "available",
  },
  {
    id: "users",
    title: "Users",
    description: "View Circle community members",
    icon: <PeopleIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/circle/users",
    status: "available",
  },
];

export default function AdminCircle() {
  const navigate = useNavigate();

  const handleFeatureClick = (feature) => {
    if (feature.status === "available") {
      navigate(feature.route);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            position: "relative",
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            background: (theme) =>
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, #1a1f2e 50%, ${theme.palette.background.default} 100%)`
                : `linear-gradient(160deg, #e8eef5 0%, #f5f8fc 30%, ${theme.palette.background.default} 70%, #e0e8f0 100%)`,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: (theme) => {
                const primary = theme.palette.primary.main;
                const secondary = theme.palette.secondary?.main || primary;
                return theme.palette.mode === "dark"
                  ? `radial-gradient(circle at 20% 20%, ${alpha(primary, 0.15)} 0%, transparent 50%), radial-gradient(circle at 80% 80%, ${alpha(secondary, 0.08)} 0%, transparent 50%)`
                  : `radial-gradient(circle at 20% 20%, ${alpha(primary, 0.06)} 0%, transparent 45%), radial-gradient(circle at 85% 70%, ${alpha(secondary, 0.08)} 0%, transparent 45%)`;
              },
              pointerEvents: "none",
            },
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1, position: "relative", zIndex: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component="a"
                  href="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Circle
                </Typography>
              </Breadcrumbs>
            </Box>

            <AdminPageHeader
              title="Circle Management"
              description="Manage Circle community events and members"
              icon={<GroupsIcon sx={{ color: "primary.main" }} />}
            />

            <Grid container spacing={3}>
              {circleFeatures.map((feature) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={feature.id}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      cursor: feature.status === "available" ? "pointer" : "default",
                      opacity: feature.status === "coming-soon" ? 0.7 : 1,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      background: (theme) =>
                        theme.palette.mode === "dark"
                          ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.1)} 100%)`
                          : `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.05)} 100%)`,
                      border: (theme) =>
                        `2px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.3 : 0.18)}`,
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? `0 4px 24px rgba(0,0,0,0.35), 0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`
                          : `0 4px 24px ${alpha(theme.palette.primary.main, 0.12)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`,
                      "&:hover": feature.status === "available"
                        ? {
                            transform: "translateY(-8px) scale(1.02)",
                            boxShadow: (theme) => {
                              const c = theme.palette.primary.main;
                              const s = theme.palette.secondary?.main || c;
                              return theme.palette.mode === "dark"
                                ? `0 28px 56px rgba(0,0,0,0.45), 0 0 0 1.5px ${alpha(s, 0.3)}`
                                : `0 28px 56px ${alpha(c, 0.2)}, 0 0 0 1.5px ${alpha(c, 0.18)}`;
                            },
                          }
                        : {},
                    }}
                    onClick={() => handleFeatureClick(feature)}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 60,
                            height: 60,
                            borderRadius: 1.5,
                            background: (theme) =>
                              alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.2 : 0.12),
                            "& .MuiSvgIcon-root": { fontSize: 36 },
                          }}
                        >
                          {feature.icon}
                        </Box>
                      </Box>
                      <Typography variant="h6" component="h2" fontWeight="bold" gutterBottom>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </CardContent>
                    {feature.status === "available" && (
                      <CardActions sx={{ px: 2, pb: 2 }}>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(feature.route);
                          }}
                          sx={{
                            py: 1.25,
                            fontWeight: 600,
                            letterSpacing: "0.02em",
                            transition: "all 0.25s ease",
                            "&:hover": {
                              transform: "translateY(-1px)",
                              boxShadow: (theme) =>
                                `0 6px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
                            },
                          }}
                        >
                          Open
                        </Button>
                      </CardActions>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
          <Footer />
        </Box>
      </Box>
    </>
  );
}
